import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeaders, useAuth } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';
import { canEditModule } from '../lib/permissions';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Upload, Eye, Search, FileSpreadsheet, Package, IndianRupee, Warehouse, ClipboardList, RefreshCw, Wand2, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight } from 'lucide-react';
import SKUMappingDialog from '../components/SKUMappingDialog';
import { ImportPreviewDialog } from '../components/ImportPreviewDialog';
import { RetailPOTemplateImport } from '../components/RetailPOTemplateImport';
import { LoadError, errorMessage } from '../components/LoadError';
import { StatusBadge } from '../components/StatusBadge';
import { StatStrip } from '../components/StatStrip';
import { UrgentPoBanner } from '../components/UrgentPoBanner';
import { shortMoney } from '../components/sales/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REGION_COLORS = {
  Bengaluru: '#5856D6', Mumbai: '#FF9500', Hyderabad: '#AF52DE',
  Gurgaon: '#007AFF', Chennai: '#34C759',
};

// Sort header icon: the active column shows its direction; others show a faint two-way arrow.
const SortIcon = ({ field, sortField, sortDirection }) => {
  if (sortField !== field) return <ArrowUpDown className="h-3 w-3 opacity-40" aria-hidden="true" />;
  const Icon = sortDirection === 'asc' ? ArrowUp : ArrowDown;
  return <Icon className="h-3 w-3" aria-hidden="true" />;
};

export const RetailPOPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEditRetailPos = canEditModule(user, 'retail_pos');
  const canEditMappings = canEditModule(user, 'sku_aliases');
  const { country } = useCountry();
  const [pos, setPos] = useState([]);
  const [fcs, setFcs] = useState([]);
  const [fcDemand, setFcDemand] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importingItems, setImportingItems] = useState(false);
  const [bulkUnmatched, setBulkUnmatched] = useState([]);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [showFcDemand, setShowFcDemand] = useState(false);
  // Dry-run preview awaiting confirmation: { kind: 'export' | 'items', file, data }
  const [importPreview, setImportPreview] = useState(null);
  const fileRef = useRef(null);
  const itemsFileRef = useRef(null);

  const [fcFilter, setFcFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  
  // Pagination & Sorting
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [sortField, setSortField] = useState('order_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const ariaSort = field => (sortField === field ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined);

  const fetchData = useCallback(async () => {
    // allSettled so a failing FC lookup doesn't hide the PO list (and vice versa)
    const [posRes, fcRes, fcDemandRes] = await Promise.allSettled([
      axios.get(`${API}/retail-pos?country=${country}`, { headers: getAuthHeaders() }),
      axios.get(`${API}/shipment-planning/fc-regions?platform=AMAZON`, { headers: getAuthHeaders() }),
      axios.get(`${API}/shipment-planning/fc-demand-summary?country=${country}`, { headers: getAuthHeaders() })
    ]);
    if (posRes.status === 'fulfilled') {
      setPos(posRes.value.data);
      setLoadError(null);
    } else {
      setLoadError(posRes.reason);
    }
    if (fcRes.status === 'fulfilled') setFcs(fcRes.value.data);
    if (fcDemandRes.status === 'fulfilled') setFcDemand(fcDemandRes.value.data || []);
    if (posRes.status === 'fulfilled' && (fcRes.status === 'rejected' || fcDemandRes.status === 'rejected')) {
      toast.error(`Couldn't load FC details: ${errorMessage(fcRes.reason || fcDemandRes.reason)}`);
    }
    setLoading(false);
  }, [country]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const retryFetch = () => { setLoading(true); fetchData(); };

  const regionOf = useMemo(() => {
    const m = {};
    fcs.forEach(f => { m[f.fc_code] = f.region_name; });
    return m;
  }, [fcs]);

  const fcDisplayName = useMemo(() => {
    const m = {};
    fcs.forEach(f => { 
      m[f.fc_code] = f.city ? `${f.fc_code} - ${f.city}` : f.fc_code; 
    });
    return m;
  }, [fcs]);

  const postImport = (kind, file, dryRun) => {
    const form = new FormData();
    form.append('file', file);
    const params = new URLSearchParams({ country });
    if (dryRun) params.set('dry_run', 'true');
    const endpoint = kind === 'export' ? 'import-po-export' : 'import-po-items-bulk';
    return axios.post(`${API}/retail-pos/${endpoint}?${params}`, form, { headers: getAuthHeaders() });
  };

  // Step 1: picking a file only runs a dry-run preview; nothing is saved until confirmed.
  const previewImport = async (kind, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const setBusy = kind === 'export' ? setImporting : setImportingItems;
    setBusy(true);
    try {
      const res = await postImport(kind, file, true);
      setImportPreview({ kind, file, data: res.data });
    } catch (err) {
      toast.error(errorMessage(err, kind === 'export' ? 'Import failed' : 'Bulk items import failed'));
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };

  const handleImport = (e) => previewImport('export', e);
  const handleBulkItemsImport = (e) => previewImport('items', e);

  // Step 2: re-send the same file without dry_run. Throwing keeps the preview open.
  const confirmImport = async () => {
    if (!importPreview) return;
    const { kind, file } = importPreview;
    try {
      const res = await postImport(kind, file, false);
      toast.success(res.data.message);
      if (kind === 'items' && res.data.unmatched_details?.length > 0) {
        setBulkUnmatched(res.data.unmatched_details);
        setMappingOpen(true);
        toast.warning(`${res.data.unmatched_details.length} vendor code(s) need mapping — dialog opened.`);
      }
      fetchData();
    } catch (err) {
      toast.error(errorMessage(err, kind === 'export' ? 'Import failed' : 'Bulk items import failed'));
      throw err;
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setCurrentPage(1); // Reset to first page on sort
  };

  const filtered = useMemo(() => {
    let r = [...pos];
    
    // Apply filters
    if (fcFilter !== 'ALL') r = r.filter(p => p.ship_to_fc === fcFilter);
    if (statusFilter !== 'ALL') r = r.filter(p => p.status === statusFilter);
    if (search) r = r.filter(p => p.po_number?.toLowerCase().includes(search.toLowerCase()));
    
    // Apply sorting
    r.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortField) {
        case 'po_number':
          aVal = a.po_number || '';
          bVal = b.po_number || '';
          break;
        case 'order_date':
          aVal = a.order_date || a.po_date || '';
          bVal = b.order_date || b.po_date || '';
          break;
        case 'deadline':
          aVal = a.cancellation_deadline || '';
          bVal = b.cancellation_deadline || '';
          break;
        case 'remaining':
          aVal = a.remaining_qty ?? ((a.total_qty || 0) - (a.total_dispatched || 0));
          bVal = b.remaining_qty ?? ((b.total_qty || 0) - (b.total_dispatched || 0));
          break;
        case 'value':
          aVal = a.total_accepted_cost || 0;
          bVal = b.total_accepted_cost || 0;
          break;
        case 'received_value':
          aVal = (a.received_qty || 0) * (a.total_accepted_cost || 0) / Math.max(a.accepted_qty || 1, 1);
          bVal = (b.received_qty || 0) * (b.total_accepted_cost || 0) / Math.max(b.accepted_qty || 1, 1);
          break;
        default:
          aVal = a.order_date || a.po_date || '';
          bVal = b.order_date || b.po_date || '';
      }
      
      const order = Number(aVal > bVal) - Number(aVal < bVal);
      return sortDirection === 'asc' ? order : -order;
    });
    
    return r;
  }, [pos, fcFilter, statusFilter, search, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filtered.slice(startIndex, startIndex + itemsPerPage);
  }, [filtered, currentPage, itemsPerPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [fcFilter, statusFilter, search]);

  const kpis = useMemo(() => {
    const open = pos.filter(p => ['CONFIRMED', 'OPEN', 'PARTIAL'].includes(p.status));
    return {
      openCount: open.length,
      remaining: open.reduce((s, p) => s + (p.remaining_qty ?? ((p.total_qty || 0) - (p.total_dispatched || 0))), 0),
      value: open.reduce((s, p) => s + (p.total_accepted_cost || 0), 0),
      fcCount: new Set(open.map(p => p.ship_to_fc).filter(Boolean)).size,
    };
  }, [pos]);

  const usedFcs = useMemo(() => [...new Set(pos.map(p => p.ship_to_fc).filter(Boolean))].sort(), [pos]);

  return (
    <div className="space-y-5 sp-body" data-testid="retail-po-page">
      {/* Header */}
      <div className="sp-rise flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">Retail Purchase Orders</h1>
          <p className="mt-1 text-[13px] text-gray-500">Amazon Vendor Central POs · one PO per fulfillment center</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canEditMappings && (
          <Button
            onClick={() => { setBulkUnmatched([]); setMappingOpen(true); }}
            variant="outline"
            className="h-9 px-3 text-[13px] font-semibold rounded-lg border-[#B45309]/30 text-[#B45309] hover:bg-amber-50 active:scale-95 transition-transform"
            data-testid="open-sku-mapping-manager-btn"
          >
            <Wand2 className="h-4 w-4 mr-2" aria-hidden="true" /> Map PO codes
          </Button>
          )}
          {canEditRetailPos && (
          <>
          <RetailPOTemplateImport country={country} onImported={fetchData} />
          <input ref={itemsFileRef} type="file" accept=".xls,.xlsx" onChange={handleBulkItemsImport} className="hidden" data-testid="po-items-bulk-file-input" />
          <Button
            onClick={() => itemsFileRef.current?.click()}
            disabled={importingItems}
            variant="outline"
            className="h-9 px-4 text-[13px] font-semibold rounded-lg border-[#007AFF]/30 text-[#007AFF] hover:bg-blue-50 active:scale-95 transition-transform"
            data-testid="import-po-items-bulk-btn"
          >
            {importingItems ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}
            Import PO Items
          </Button>
          <input ref={fileRef} type="file" accept=".xls,.xlsx" onChange={handleImport} className="hidden" data-testid="po-export-file-input" />
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={importing}
            className="h-9 px-4 bg-[#007AFF] hover:bg-[#005bb5] active:scale-95 text-white text-[13px] font-semibold rounded-lg transition-transform"
            data-testid="import-po-export-btn"
          >
            {importing ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            Import PO Export
          </Button>
          </>
          )}
        </div>
      </div>
      <UrgentPoBanner country={country} />

      {/* Summary */}
      <StatStrip testId="po-summary" items={[
        { key: 'open', label: 'open POs', tone: 'blue', value: loadError ? '–' : kpis.openCount },
        { key: 'units', label: 'units still to ship', tone: 'gray', value: loadError ? '–' : kpis.remaining.toLocaleString('en-IN'), hint: 'Not yet dispatched or received' },
        { key: 'value', label: 'open PO value', tone: 'gray', value: loadError ? '–' : shortMoney(kpis.value), hint: loadError ? undefined : `₹${kpis.value.toLocaleString('en-IN')}` },
        { key: 'fcs', label: 'FCs covered', tone: 'gray', value: loadError ? '–' : kpis.fcCount, hint: 'Destination fulfillment centers' },
      ]} />

      {/* FC-wise Demand Summary */}
      {fcDemand.length > 0 && (
        <div className="sp-card sp-rise rounded-xl p-4" style={{ animationDelay: '60ms' }}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[13px] font-bold text-gray-700 uppercase tracking-wide">FC-Wise Demand</h3>
            <Button
              onClick={() => setShowFcDemand(!showFcDemand)}
              variant="ghost"
              size="sm"
              className="h-7 text-[11px]"
            >
              {showFcDemand ? 'Hide' : 'Show'} ({fcDemand.length} FCs)
            </Button>
          </div>
          
          {showFcDemand && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
              {fcDemand.slice(0, 6).map((fc) => (
                <div key={fc.fc_code} className="bg-gradient-to-br from-gray-50 to-white border border-gray-100 rounded-lg p-3">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="font-bold text-[13px] text-gray-800">{fc.display_name}</div>
                      <div className="text-[10px] text-gray-500 uppercase tracking-wide">{fc.region}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[11px] font-bold text-[#0062CC]">{fc.open_pos} POs</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-2 pt-2 border-t border-gray-100">
                    <div>
                      <div className="text-[10px] text-gray-500">Remaining</div>
                      <div className="text-[13px] font-bold sp-num">{fc.remaining_units.toLocaleString('en-IN')}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-gray-500">Value</div>
                      <div className="text-[13px] font-bold sp-num">₹{(fc.total_value / 100000).toFixed(1)}L</div>
                    </div>
                    {fc.unmatched_items > 0 && (
                      <div className="ml-auto">
                        <div className="text-[10px] text-amber-800 font-bold">{fc.unmatched_items} unmatched</div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="sp-glass sp-rise rounded-xl border border-black/5 shadow-sm px-4 py-3 flex flex-wrap items-center gap-3" style={{ animationDelay: '80ms' }} data-testid="po-filters">
        <div className="relative">
          <Search className="h-3.5 w-3.5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" aria-hidden="true" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search PO number" aria-label="Search PO number" className="h-9 w-52 pl-8 text-[12px] bg-white" data-testid="po-search-input" />
        </div>
        <Select value={fcFilter} onValueChange={setFcFilter}>
          <SelectTrigger className="h-9 w-40 text-[12px] bg-white" data-testid="fc-filter">
            <SelectValue placeholder="FC" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL" className="text-[12px]">All FCs</SelectItem>
            {usedFcs.map(fc => (
              <SelectItem key={fc} value={fc} className="text-[12px]">
                {fcDisplayName[fc] || fc}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-9 w-36 text-[12px] bg-white" data-testid="status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL" className="text-[12px]">All Status</SelectItem>
            <SelectItem value="CONFIRMED" className="text-[12px]">Confirmed</SelectItem>
            <SelectItem value="OPEN" className="text-[12px]">Open</SelectItem>
            <SelectItem value="PARTIAL" className="text-[12px]">Partial</SelectItem>
            <SelectItem value="CLOSED" className="text-[12px]">Closed</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto text-[11px] text-gray-500 flex flex-wrap items-center gap-3">
          <span>
            Showing <span className="sp-num font-semibold text-gray-600">{paginatedData.length}</span> of <span className="sp-num font-semibold text-gray-600">{filtered.length}</span> POs
          </span>
          <Select value={String(itemsPerPage)} onValueChange={(val) => { setItemsPerPage(Number(val)); setCurrentPage(1); }}>
            <SelectTrigger className="h-7 w-20 text-[11px] bg-white" aria-label="POs per page">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10" className="text-[11px]">10/page</SelectItem>
              <SelectItem value="25" className="text-[11px]">25/page</SelectItem>
              <SelectItem value="50" className="text-[11px]">50/page</SelectItem>
              <SelectItem value="100" className="text-[11px]">100/page</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="sp-card sp-rise rounded-xl overflow-hidden shadow-sm" style={{ animationDelay: '120ms' }}>
        {loadError ? (
          <LoadError
            what="purchase orders"
            error={loadError}
            onRetry={retryFetch}
            retrying={loading}
            className="m-4"
            testId="po-list-load-error"
          />
        ) : loading ? (
          <div role="status" className="text-center py-14 text-gray-500 text-[13px]">Loading…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px] sp-grid-table" data-testid="po-table">
              <thead>
                <tr className="bg-gray-50/70 text-gray-500">
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em]" aria-sort={ariaSort('po_number')}>
                    <button type="button" onClick={() => handleSort('po_number')} className="flex items-center gap-1 hover:text-gray-600">
                      PO Number <SortIcon field="po_number" sortField={sortField} sortDirection={sortDirection} />
                    </button>
                  </th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em]">Ship-to FC</th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em]" aria-sort={ariaSort('order_date')}>
                    <button type="button" onClick={() => handleSort('order_date')} className="flex items-center gap-1 hover:text-gray-600">
                      Ordered <SortIcon field="order_date" sortField={sortField} sortDirection={sortDirection} />
                    </button>
                  </th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em]">Ship Window</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em]">Items</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em]">Requested</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em]">Accepted</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em]">Received</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em]" aria-sort={ariaSort('remaining')}>
                    <button type="button" onClick={() => handleSort('remaining')} className="flex items-center gap-1 hover:text-gray-600 ml-auto">
                      Remaining <SortIcon field="remaining" sortField={sortField} sortDirection={sortDirection} />
                    </button>
                  </th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em]" aria-sort={ariaSort('value')}>
                    <button type="button" onClick={() => handleSort('value')} className="flex items-center gap-1 hover:text-gray-600 ml-auto">
                      Value <SortIcon field="value" sortField={sortField} sortDirection={sortDirection} />
                    </button>
                  </th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em]" aria-sort={ariaSort('received_value')}>
                    <button type="button" onClick={() => handleSort('received_value')} className="flex items-center gap-1 hover:text-gray-600 ml-auto">
                      Received Value <SortIcon field="received_value" sortField={sortField} sortDirection={sortDirection} />
                    </button>
                  </th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em]" aria-sort={ariaSort('deadline')}>
                    <button type="button" onClick={() => handleSort('deadline')} className="flex items-center gap-1 hover:text-gray-600">
                      Deadline <SortIcon field="deadline" sortField={sortField} sortDirection={sortDirection} />
                    </button>
                  </th>
                  <th className="text-center px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em]">Status</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em]"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((po, index) => {
                  const region = regionOf[po.ship_to_fc];
                  const color = REGION_COLORS[region] || '#8E8E93';
                  return (
                    <tr key={po.po_number} className="cursor-pointer" onClick={() => navigate(`/retail-pos/${po.po_number}`)} data-testid={`po-row-${index}`}>
                      <td className="px-4 py-3">
                        <span className="sp-num font-bold text-gray-800">{po.po_number}</span>
                        {po.items_imported && <span className="ml-2 text-[10px] font-bold text-green-700 uppercase">Items ✓</span>}
                      </td>
                      <td className="px-4 py-3">
                        {po.ship_to_fc ? (
                          <span className="sp-num px-2 py-0.5 rounded-md text-[11px] font-bold text-white" style={{ background: color }}>
                            {fcDisplayName[po.ship_to_fc] || po.ship_to_fc}
                          </span>
                        ) : <span className="text-gray-500">—</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-500">{po.order_date || po.po_date}</td>
                      <td className="px-4 py-3 sp-num text-[11px] text-gray-500">
                        {po.window_start ? `${po.window_start} → ${po.window_end}` : '—'}
                      </td>
                      <td className="px-4 py-3 text-right sp-num">{po.item_count || 0}</td>
                      <td className="px-4 py-3 text-right sp-num text-gray-500">{po.requested_qty ?? po.total_qty ?? 0}</td>
                      <td className="px-4 py-3 text-right sp-num font-semibold">{po.accepted_qty ?? po.total_qty ?? 0}</td>
                      <td className="px-4 py-3 text-right sp-num text-gray-500">{po.received_qty ?? po.total_dispatched ?? 0}</td>
                      <td className="px-4 py-3 text-right sp-num font-bold text-[#0062CC]">{po.remaining_qty ?? ((po.total_qty || 0) - (po.total_dispatched || 0))}</td>
                      <td className="px-4 py-3 text-right sp-num">₹{(po.total_accepted_cost || 0).toLocaleString('en-IN')}</td>
                      <td className="px-4 py-3 text-right sp-num font-semibold text-green-700">
                        ₹{(((po.received_qty || 0) * (po.total_accepted_cost || 0)) / Math.max(po.accepted_qty || 1, 1)).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </td>
                      <td className="px-4 py-3 text-gray-500">{po.cancellation_deadline || '—'}</td>
                      <td className="px-4 py-3 text-center">
                        <StatusBadge status={po.status} data-testid={`po-status-${index}`} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); navigate(`/retail-pos/${po.po_number}`); }} className="h-7 px-2 text-[11px]" aria-label={`View PO ${po.po_number}`} data-testid={`view-po-${index}`}>
                          <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="text-center py-14 text-gray-500 text-[13px]">
                <FileSpreadsheet className="h-8 w-8 text-gray-300 mx-auto mb-2" aria-hidden="true" />
                No POs found. Import your Amazon PO Export file to get started.
              </div>
            )}
          </div>
        )}
        
        {/* Pagination Controls */}
        {!loadError && totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-100 flex flex-wrap items-center justify-between gap-2">
            <div className="text-[11px] text-gray-500">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                variant="outline"
                size="sm"
                className="h-7 px-2"
                aria-label="Previous page"
              >
                <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      variant={currentPage === pageNum ? "default" : "outline"}
                      size="sm"
                      className={`h-7 w-7 px-0 text-[11px] ${currentPage === pageNum ? 'bg-gray-900 text-white' : ''}`}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              <Button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                variant="outline"
                size="sm"
                className="h-7 px-2"
                aria-label="Next page"
              >
                <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <ImportPreviewDialog
        open={!!importPreview}
        onOpenChange={(open) => { if (!open) setImportPreview(null); }}
        title={importPreview?.kind === 'items'
          ? 'Review PO items import (replaces line items of each PO in the file)'
          : 'Review PO export import'}
        fileName={importPreview?.file?.name}
        preview={importPreview?.data}
        entityLabel={importPreview?.kind === 'items' ? 'line items' : 'POs'}
        issuesNote={importPreview?.kind === 'items' ? 'Rows with errors are skipped; lines whose code is not found are imported without a SKU match.' : undefined}
        confirmLabel={importPreview?.kind === 'items' ? 'Import line items' : 'Import POs'}
        onConfirm={confirmImport}
        testId="po-import-preview"
      />

      <SKUMappingDialog
        open={mappingOpen}
        onOpenChange={setMappingOpen}
        unmatched={bulkUnmatched}
        country={country}
        onApplied={fetchData}
      />
    </div>
  );
};
